import pluralize from "pluralize";

import type { Maybe } from "src/graphql";

export enum ScheduleType {
  MANUAL = "manual",
  INTERVAL = "interval",
  CRON = "cron",
  CUSTOM = "visual_cron",
  DBT_CLOUD = "dbt",
  FIVETRAN = "fivetran",
  STREAMING = "streaming",
  MATCH_BOOSTER = "match_booster",
  JOURNEY_TRIGGERED = "journey_triggered",
  SEQUENCE = "sequence",
  IDENTITY_RESOLUTION = "idr_run",
}

export const CALENDAR_RECURRENCE_SCHEDULE_TYPE = "calendar_recurrence";
export const VALID_SCHEDULE_TIME = /^(?:[01]\d|2[0-3]):[0-5]\d$/;

export const scheduleToLabel = (schedule: ReportSchedule) => {
  switch (schedule?.type) {
    case ScheduleType.MANUAL:
      return "Manual";
    case ScheduleType.INTERVAL:
      return `Every ${schedule.schedule?.interval.quantity} ${pluralize(
        schedule.schedule?.interval.unit ?? "",
        schedule.schedule?.interval.quantity ?? 0,
      )}`;
    case ScheduleType.CRON:
      return "Cron expression";
    case ScheduleType.CUSTOM:
      return "Custom";
    case CALENDAR_RECURRENCE_SCHEDULE_TYPE:
      return "Monthly";
    case ScheduleType.MATCH_BOOSTER:
      return "Match Booster enrichment";
    case ScheduleType.DBT_CLOUD:
    case ScheduleType.FIVETRAN:
    case ScheduleType.JOURNEY_TRIGGERED:
      return "Journey-triggered";
    case ScheduleType.SEQUENCE:
      return "Sequence";
    case ScheduleType.IDENTITY_RESOLUTION:
      return "Identity Resolution";
    default:
      return null;
  }
};

export function scheduleTypeToLabel(scheduleType: ScheduleType | null) {
  switch (scheduleType) {
    case ScheduleType.MANUAL || null:
      return "Manual";
    case ScheduleType.INTERVAL:
      return "Interval";
    case ScheduleType.CRON:
      return "Cron";
    case ScheduleType.CUSTOM:
      return "Custom";
    case ScheduleType.DBT_CLOUD:
      return "dbt Cloud";
    case ScheduleType.FIVETRAN:
      return "Fivetran";
    default:
      return "Unknown";
  }
}

interface BaseSchedule {
  type: ScheduleType;
  /**
   * Wall-clock date the user picked, in `YYYY-MM-DD` form, for CEP
   * campaigns. Resolved against `timezone` (fixed mode) or UTC+14
   * (recipient_local) at scheduler evaluation time. Legacy rows may
   * still hold a full ISO timestamp; the BE tolerates both shapes.
   */
  startDate?: string | null;
  endDate?: string | null;
  /**
   * Wall-clock time the user picked, in `HH:mm` (24h) form, for CEP
   * campaigns. Always set alongside `startDate` from the First send
   * box.
   */
  startTime?: string;
  /**
   * IANA timezone for CEP campaigns running in "Same time for everyone"
   * (fixed) mode. The marketer picks this in the schedule UI; the
   * orchestrator anchors quiet-hours and cron evaluation to it. Other
   * schedule consumers (syncs, journeys, etc.) can ignore it.
   */
  timezone?: string;
  /**
   * How long an in-app message stays in one profile's inbox, counted from when
   * that profile received it — not from when the campaign ends. Absent means it
   * stays until the profile leaves the audience or the campaign stops. CEP
   * in-app campaigns only; other schedule consumers can ignore it.
   */
  messageExpiration?: InAppMessageExpiration | null;
}

/** See {@link BaseSchedule.messageExpiration}. */
export interface InAppMessageExpiration {
  quantity: number;
  unit: ScheduleIntervalUnit;
}

export interface ManualSchedule extends BaseSchedule {
  type: ScheduleType.MANUAL;
}

export interface JourneyTriggeredSchedule extends BaseSchedule {
  type: ScheduleType.JOURNEY_TRIGGERED;
}

export interface StreamingSchedule extends BaseSchedule {
  type: ScheduleType.STREAMING;
}

export interface IntervalSchedule extends BaseSchedule {
  type: ScheduleType.INTERVAL;
  schedule?: {
    interval: ScheduleInterval;
  };
}
export enum ScheduleIntervalUnit {
  MINUTE = "minute",
  HOUR = "hour",
  DAY = "day",
  WEEK = "week",
}

export interface CronSchedule extends BaseSchedule {
  type: ScheduleType.CRON;
  schedule?: {
    expression?: string;
  };
}

export interface VisualCronSchedule extends BaseSchedule {
  type: ScheduleType.CUSTOM;
  schedule?: {
    expressions?: ScheduleExpression[];
  };
}

export interface CalendarRecurrenceSchedule {
  type: typeof CALENDAR_RECURRENCE_SCHEDULE_TYPE;
  startDate?: string | null;
  endDate?: string | null;
  schedule: {
    unit: "month";
    days: number[];
    time: string;
    overflow: "last_day";
    timezone: "UTC";
  };
}

export interface DbtCloudSchedule extends BaseSchedule {
  type: ScheduleType.DBT_CLOUD;
  schedule?: {
    account?: {
      id: string;
      name: string;
    };
    job?: {
      id: string;
      name: string;
    } | null;
  };
}

export type FivetranScheduleTrigger = {
  id: string;
  type: string;
  name: string;
  fivetran_id: Maybe<string>;
};

export interface FivetranSchedule extends BaseSchedule {
  type: ScheduleType.FIVETRAN;
  schedule?: {
    groupId: string;
    groupName: string;
    triggers: FivetranScheduleTrigger[];
  };
}

export interface MatchBoosterSchedule extends BaseSchedule {
  type: ScheduleType.MATCH_BOOSTER;
}

export interface JourneyTriggeredSchedule extends BaseSchedule {
  type: ScheduleType.JOURNEY_TRIGGERED;
}

export interface SequenceSchedule extends BaseSchedule {
  type: ScheduleType.SEQUENCE;
}

export interface IdrRunSchedule extends BaseSchedule {
  type: ScheduleType.IDENTITY_RESOLUTION;
}

export type Schedule =
  | ManualSchedule
  | IntervalSchedule
  | CronSchedule
  | VisualCronSchedule
  | DbtCloudSchedule
  | FivetranSchedule
  | StreamingSchedule
  | MatchBoosterSchedule
  | JourneyTriggeredSchedule
  | SequenceSchedule
  | IdrRunSchedule;

export type ReportSchedule = Schedule | CalendarRecurrenceSchedule;

const SCHEDULE_TYPES = new Set<string>(Object.values(ScheduleType));

export function isScheduleConfig(value: unknown): value is Schedule {
  return (
    typeof value === "object" &&
    value !== null &&
    "type" in value &&
    typeof value.type === "string" &&
    SCHEDULE_TYPES.has(value.type)
  );
}

export interface ScheduleInterval {
  quantity?: number | null;
  unit?: ScheduleIntervalUnit;
}

export interface ScheduleExpression {
  time: string;
  days?: Record<string, boolean | undefined>;
}
